// src/components/layouts/AppLayout.tsx
import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Menu, X, LogOut, Users, ChevronDown, Building2 } from 'lucide-react';
import { routes } from '../../routes';
import { useAuth } from '../../contexts/AuthContext';
import UserManagement from '../UserManagement';
import CompanyManagement from '../CompanyManagement';
import CompanySwitcher from '../CompanySwitcher';
import ActingBanner from '../ActingBanner';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, logout, actingAs } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showCompanyManagement, setShowCompanyManagement] = useState(false);

  const isSuperAdmin = user?.role === 'SuperAdmin';
  
  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
  };

  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <img src="/pinage-logo.png" alt="Pinage" className="h-8 w-auto" />
              </div>
              <nav className="mt-5 px-2 space-y-1">
                {routes.map((route) => (
                  <Link
                    key={route.path}
                    to={route.path}
                    className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${
                      isActiveRoute(route.path)
                        ? 'bg-blue-100 text-blue-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <route.icon className="mr-4 h-6 w-6" />
                    {route.name}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <img src="/pinage-logo.png" alt="Pinage" className="h-8 w-auto" />
              </div>
              <nav className="mt-5 flex-1 px-2 bg-white space-y-1">
                {routes.map((route) => (
                  <Link
                    key={route.path}
                    to={route.path}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActiveRoute(route.path)
                        ? 'bg-blue-100 text-blue-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <route.icon className="mr-3 h-5 w-5" />
                    {route.name}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Says whose devices and content you are about to change. Renders
            nothing unless a super admin is working inside another company. */}
        <ActingBanner />

        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex">
                <button
                  className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none md:hidden"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-6 w-6" />
                </button>
                <div className="flex items-center">
                  <h1 className="text-xl font-semibold text-gray-900">
                    {routes.find(route => isActiveRoute(route.path))?.name || 'Dashboard'}
                  </h1>
                </div>
              </div>

              {/* User menu */}
              <div className="flex items-center gap-4">
                {/* Renders nothing for anyone but a super admin. */}
                <CompanySwitcher />

                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center text-sm text-gray-700 hover:text-gray-900 focus:outline-none"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {user?.firstName?.[0]}{user?.lastName?.[0]}
                          </span>
                        </div>
                      </div>
                      <div className="hidden md:block">
                        <div className="text-sm font-medium text-gray-900">
                          {user?.firstName} {user?.lastName}
                        </div>
                        <div className="text-xs text-gray-500">{user?.companyName}</div>
                      </div>
                      <ChevronDown className="h-4 w-4" />
                    </div>
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                        <div className="font-medium">{user?.firstName} {user?.lastName}</div>
                        <div className="text-xs text-gray-500">{user?.email}</div>
                      </div>
                      
                      <button
                        onClick={() => {
                          setShowUserManagement(true);
                          setUserMenuOpen(false);
                        }}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Users className="mr-3 h-4 w-4" />
                        Manage Users
                      </button>
                      
                      {/* Hidden while acting. Managing companies - including
                          deleting one - from inside a customer's context invites
                          exactly the mistake the banner exists to prevent. */}
                      {isSuperAdmin && !actingAs && (
                        <button
                          onClick={() => {
                            setShowCompanyManagement(true);
                            setUserMenuOpen(false);
                          }}
                          className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Building2 className="mr-3 h-4 w-4" />
                          Manage Companies
                        </button>
                      )}
                      
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <LogOut className="mr-3 h-4 w-4" />
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>

      {/* User Management Modal */}
      {showUserManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl">
            <UserManagement onClose={() => setShowUserManagement(false)} />
          </div>
        </div>
      )}

      {/* Company Management Modal */}
      {showCompanyManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-6xl">
            <CompanyManagement onClose={() => setShowCompanyManagement(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AppLayout;